import {
  BlocksAssignmentNotAllowed,
  ClassroomClash,
  ClassroomsAssignmentNotAllowed,
  InstructorAssignmentNotAllowed,
  InstructorClash,
  InstructorExceededWeeklyBlocks,
  IntervalsAssignmentNotAllowed,
  InvalidBlock,
  InvalidBlockForInstructor,
  InvalidBuildingForCourse,
  InvalidClassroomCapacity,
  InvalidClassroomsCapacityAssignment,
  InvalidComponentSize,
  InvalidFieldValidation,
  InvalidGroupEnrollment,
  InvalidInstructorTravel,
  InvalidInterval,
  InvalidLinkChanges,
  InvalidPackageTravel,
  LinkClash,
  PackageClash,
  BookingClassroomClash,
  SectionClash,
  StudentClash,
  ExternalGroupValidation,
  InvalidPastSessionEdition,
  InvalidSessionInHoliday,
  InstructorAssignmentRequired,
  Holiday,
} from "../ISchema";
import i18n from "i18next";

export type ValidationErrorType =
  | ClassroomClash
  | BookingClassroomClash
  | InstructorClash
  | LinkClash
  | SectionClash
  | PackageClash
  | InvalidInstructorTravel
  | InvalidPackageTravel
  | InvalidBuildingForCourse
  | InvalidBlock
  | InvalidInterval
  | InvalidBlockForInstructor
  | StudentClash
  | InvalidComponentSize
  | InvalidClassroomCapacity
  | InvalidClassroomsCapacityAssignment
  | InvalidLinkChanges
  | InvalidGroupEnrollment
  | InvalidFieldValidation
  | InstructorAssignmentNotAllowed
  | IntervalsAssignmentNotAllowed
  | BlocksAssignmentNotAllowed
  | ClassroomsAssignmentNotAllowed
  | InstructorExceededWeeklyBlocks
  | ExternalGroupValidation
  | InvalidPastSessionEdition
  | InvalidSessionInHoliday
  | InstructorAssignmentRequired;

export interface IValidationError {
  incidence: ValidationErrorType | any;
}

export const errors = {
  ClassroomClash: i18n.t("assignation-edit.incidence-table.validations.classroom-clash.title"),
  BookingClassroomClash: i18n.t(
    "assignation-edit.incidence-table.validations.booking-classroom-clash.title",
  ),
  InstructorClash: i18n.t("assignation-edit.incidence-table.validations.instructor-clash.title"),
  LinkClash: i18n.t("assignation-edit.incidence-table.validations.link-clash.title"),
  SectionClash: i18n.t("assignation-edit.incidence-table.validations.section-clash.title"),
  PackageClash: i18n.t("assignation-edit.incidence-table.validations.package-clash.title"),
  InvalidInstructorTravel: i18n.t(
    "assignation-edit.incidence-table.validations.invalid-instructor-travel.title",
  ),
  InvalidPackageTravel: i18n.t(
    "assignation-edit.incidence-table.validations.invalid-package-travel.title",
  ),
  InvalidBuildingForCourse: i18n.t(
    "assignation-edit.incidence-table.validations.invalid-building-for-course.title",
  ),
  InvalidBlock: i18n.t("assignation-edit.incidence-table.validations.invalid-block.title"),
  InvalidBlockForInstructor: i18n.t(
    "assignation-edit.incidence-table.validations.invalid-block-for-instructor.title",
  ),
  ExceededAssignableBlocksUpcomingYear: i18n.t(
    "assignation-edit.incidence-table.validations.exceeded-assignable-blocks-upcoming-year.title",
  ),
  ExceededAssignableBlocksCurrentYear: i18n.t(
    "assignation-edit.incidence-table.validations.exceeded-assignable-blocks-current-year.title",
  ),
};

class SessionByType {
  private incidence: ValidationErrorType;
  constructor(error: IValidationError) {
    this.incidence = error.incidence;
  }

  private packageIncidenceMsg = (incidence: PackageClash) => {
    const complete =
      incidence.package.complete !== null
        ? incidence.package.complete
          ? `${i18n.t("assignation-edit.incidence-table.validations.package-clash.complete")}, `
          : `${i18n.t("assignation-edit.incidence-table.validations.package-clash.incomplete")}, `
        : "";
    const type = incidence.package.type !== null ? "*" : "";

    const countVacancies = incidence.package.vacancies ? incidence.package.vacancies : 0;
    const vacancies = countVacancies
      ? `${countVacancies} ${i18n.t(
          "assignation-edit.incidence-table.validations.package-clash.vacancies",
        )}`
      : "";

    const opt =
      incidence.package.hasPackageRules !== null
        ? incidence.package.hasPackageRules
          ? `${i18n.t("assignation-edit.incidence-table.validations.package-clash.required")}, `
          : `${i18n.t("assignation-edit.incidence-table.validations.package-clash.optional")}, `
        : "";
    const msgTitle = errors[incidence.__typename];
    return `${type}  ${msgTitle} ${incidence.package.code} (${type} ${complete} ${opt} ${vacancies}) `;
  };

  public getMessage() {
    switch (this.incidence.__typename) {
      case "PackageClash":
        return this.packageIncidenceMsg(this.incidence);
      case "ClassroomClash":
        return `${errors[this.incidence.__typename]} ${this.incidence?.classroom?.code}`;
      case "BookingClassroomClash":
        return `${errors[this.incidence.__typename]} ${this.incidence?.classroom?.code}`;
      case "InstructorClash":
        return `${errors[this.incidence.__typename]} ${this.incidence?.instructor?.code}`;
      case "InvalidBlockForInstructor":
        return i18n.t(
          "assignation-edit.incidence-table.validations.invalid-block-for-instructor.description",
          {
            code: this.incidence?.instructor?.code,
          },
        );
      case "StudentClash":
        const textCountStudent = `${i18n.t(
          "assignation-edit.incidence-table.validations.student-clash.student",
        )}${this.incidence.count > 1 ? "s" : ""}`;
        return `${i18n.t(
          "assignation-edit.incidence-table.validations.student-clash.description",
        )} - ${this.incidence?.count} ${textCountStudent}`;
      case "InstructorExceededWeeklyBlocks":
        return i18n.t(
          "assignation-edit.incidence-table.validations.instructor-exceeded-weekly-blocks.description",
          {
            instructorCode: this.incidence?.instructor?.code,
            assignableBlocks: this.incidence.assignableBlocks,
            exceededBlocks: this.incidence.exceededBlocks,
          },
        );
      case "InstructorAssignmentNotAllowed":
        return i18n.t(
          "assignation-edit.incidence-table.validations.instructor-assignment-not-allowed.description",
        );
      case "IntervalsAssignmentNotAllowed":
        return i18n.t(
          "assignation-edit.incidence-table.validations.intervals-assignment-not-allowed.description",
        );
      case "BlocksAssignmentNotAllowed":
        return i18n.t(
          "assignation-edit.incidence-table.validations.blocks-assignment-not-allowed.description",
        );
      case "ClassroomsAssignmentNotAllowed":
        return i18n.t(
          "assignation-edit.incidence-table.validations.classrooms-assignment-not-allowed.description",
        );
      case "ExternalGroupValidation":
        return i18n.t(
          "assignation-edit.incidence-table.validations.external-group-validation.description",
        );
      case "InvalidClassroomsCapacityAssignment":
        return i18n.t(
          "assignation-edit.incidence-table.validations.invalid-classrooms-capacity-assignment.description",
        );
      case "InvalidInstructorTravel":
        return i18n.t(
          "assignation-edit.incidence-table.validations.invalid-instructor-travel.description",
          {
            fromBuildingName: this.incidence?.withFollowing?.fromBuilding?.name,
            fromBuildingCode: this.incidence?.withFollowing?.fromBuilding?.code,
            toBuildingName: this.incidence?.withFollowing?.toBuilding?.name,
            toBuildingCode: this.incidence?.withFollowing?.toBuilding?.code,
          },
        );
      case "InvalidPastSessionEdition":
        return i18n.t(
          "assignation-edit.incidence-table.validations.invalid-past-session-edition.description",
        );
      case "InstructorAssignmentRequired":
        return i18n.t(
          "assignation-edit.incidence-table.validations.instructor-assignment-required.description",
        );
      case "InvalidSessionInHoliday":
        const holidayName = ((this?.incidence?.holiday as unknown) as Holiday)?.name;
        return this?.incidence?.allowed
          ? holidayName
          : ` ${i18n.t(
              "assignation-edit.incidence-table.validations.invalid-session-in-holiday.description",
              {
                holidayName,
              },
            )}`;

      default:
        return errors[this.incidence.__typename];
    }
  }
}

export default SessionByType;
